import { BookingInquiryCargos } from '@models/next/BookingInquiry';
import {
  BookingInquiryUseTradingAgency,
  TradeCondition,
  TruckName,
} from '@models/next/BookingInquiryEnums';
import { Country } from '@models/next/Country';
import { InvoiceDetailsFields } from '@models/next/Invoice';
import { RoutePlan } from '@models/next/RoutePlan';
import { f } from '@utils/nextForms';
import { z } from 'zod';

export const RoutePlanSimulatorType = z.enum(['BOOKING_CREATE', 'PRICE_SIMULATOR']);

export type RoutePlanSimulatorType = z.infer<typeof RoutePlanSimulatorType>;

export const SimulatorInvoiceDetails = InvoiceDetailsFields.pick({
  expense_cost: true,
  invoice_name: true,
  unit_num_type: true,
  currency: true,
  unit_price: true,
  unit_num: true,
  excise_tax_type: true,
  payer_mark: true,
  country: true,
}).extend({
  exchange_base_rate: z.number(),
  /**
   * @example "2023-01-01"
   * The value is "" when exchange_base_rate is 0 for JPY
   */
  exchange_base_date: z.string(),
});

export type SimulatorInvoiceDetails = z.infer<typeof SimulatorInvoiceDetails>;

/**
 * Summary of baggage, from FC simulator.
 */
export const SimulatorBaggage = z.object({
  total_kg_num: z.number(),
  total_m3kg: z.number(),
  total_m3_num: z.number(),
  total_m3: z.number(),
  /**
   * 基準計算重量
   */
  target_kg: z.number(),
  /**
   * "M3" | "KG", shows the property that decides the `target_weight`
   */
  base: z.string(),
});

export type SimulatorBaggage = z.infer<typeof SimulatorBaggage>;

export const SimulatorTruckInfo = z.object({
  truck_type: z.string(),
  pickup_leadtime: z.number(),
});

export type SimulatorTruckInfo = z.infer<typeof SimulatorTruckInfo>;

export const SimulatorTruckPrice = z.object({
  /**
   * Exchange type: "JPY_CNY"
   */
  pair: z.string(),
  price: z.number(),
  price2: z.number(),
  exchange_base_date: f.dateString(),
  exchange_base_rate: z.number(),
});

export type SimulatorTruckPrice = z.infer<typeof SimulatorTruckPrice>;

export const SimulatorPickup = z.object({
  baggage: SimulatorBaggage,
  zipcode: z.string(),
  truck_info: SimulatorTruckInfo,
  truck_price: SimulatorTruckPrice,
});

export type SimulatorPickup = z.infer<typeof SimulatorPickup>;

export const SimulatorTruckOptionPrice = z.object({
  price: z.number(),
  price2: z.number(),
  clearance: z.object({
    tax: z.number(),
    price: z.number(),
    peak_season_surcharge: z.number(),
    service_charge_handring: z.number(),
    service_charge_clearance: z.number(),
  }),
  import_tax: z.number(),
  total_price: z.number(),
  on_sea_price: z.number(),
  shipping_tax: z.number(),
  target_price: z.number(),
  target_tax_price: z.number(),
  non_target_tax_price: z.number(),
});

export type SimulatorTruckOptionPrice = z.infer<typeof SimulatorTruckOptionPrice>;

export const SimulatorTruckOptionLeadTime = z.object({
  /**
   * Integer in string, e.g. "1"
   */
  delivery_leadtime: f.decimal(),
});

export const SimulatorTruckOption = z.object({
  price: SimulatorTruckOptionPrice,
  lead_time: SimulatorTruckOptionLeadTime,
  truck_type: TruckName,
});

export type SimulatorTruckOption = z.infer<typeof SimulatorTruckOption>;

export const SimulatorResult = z.object({
  receiver_city: z.string(),
  delivery_days: z.number(), // 納品日 - 搬入予定日・集荷日
  target_kg: z.number(), // 基準計算重量
  simulator_delivery_days: z.number(),
  simulator_pickup_days: z.number(),
  warehouse_date: f.dateString(),
  pol_date: f.dateString(),
  pod_date: f.dateString(),
  invoice_details: SimulatorInvoiceDetails.array(),
  pickups: SimulatorPickup.array(),
  baggage: SimulatorBaggage,
  truck_options: SimulatorTruckOption.array(),
  grade: z.number(),
});

export type SimulatorResult = z.infer<typeof SimulatorResult>;

export const RoutePlanSimulator = z.object({
  route_plan: RoutePlan,
  simulator: SimulatorResult,
});

export type RoutePlanSimulator = z.infer<typeof RoutePlanSimulator>;

export const PostRoutePlanSimulator = z.object({
  pickup_date: f.dateString().nullish(),
  move_in_date: f.dateString().nullish(),
  zipcode: z.string(),
  cargos: BookingInquiryCargos,
  trade_condition: TradeCondition,
  trading_agency_payer: Country,
  use_trading_agency: BookingInquiryUseTradingAgency,
  owner_id: z.number(),
  city_cd: z.string().nullish(),
  truck_type: TruckName.nullish(),
});

export type PostRoutePlanSimulator = z.infer<typeof PostRoutePlanSimulator>;

export const RoutePlanSimulatorStatus = z.object({
  count: z.number(),
  last_used: z.string(),
});

export type RoutePlanSimulatorStatus = z.infer<typeof RoutePlanSimulatorStatus>;
